import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../services/supabaseClient';

function parseHashParams(hash) {
  const raw = (hash || '').startsWith('#') ? (hash || '').slice(1) : (hash || '');
  const params = new URLSearchParams(raw);
  return {
    type: params.get('type'),
    accessToken: params.get('access_token'),
    refreshToken: params.get('refresh_token'),
    expiresIn: params.get('expires_in'),
    tokenType: params.get('token_type'),
  };
}

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const hashState = useMemo(() => parseHashParams(window.location?.hash), []);
  const isRecoveryLink = hashState?.type === 'recovery' || !!hashState?.accessToken;
  const [hasRecoverySession, setHasRecoverySession] = useState(false);

  useEffect(() => {
    let isMounted = true;

    // If we arrived via a recovery link with tokens in the URL hash, hydrate a session.
    // This makes `supabase.auth.updateUser({ password })` work reliably.
    const hydrateFromHash = async () => {
      try {
        if (hashState?.accessToken && hashState?.refreshToken) {
          const { error: sessionError } = await supabase?.auth?.setSession?.({
            access_token: hashState.accessToken,
            refresh_token: hashState.refreshToken,
          });
          if (sessionError) {
            console.log('Failed to set session from recovery hash:', sessionError);
          } else {
            // Remove hash from URL to avoid leaking tokens in the address bar/history
            try {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            } catch {
              // ignore
            }
          }
        }
      } catch (e) {
        console.log('Recovery hash hydration failed:', e);
      }
    };

    const checkSession = async () => {
      try {
        const { data } = await supabase?.auth?.getSession?.();
        if (!isMounted) return;
        setHasRecoverySession(!!data?.session);
      } catch {
        // ignore
      }
    };

    hydrateFromHash();
    checkSession();

    const { data: sub } = supabase?.auth?.onAuthStateChange?.((_event, session) => {
      if (!isMounted) return;
      setHasRecoverySession(!!session);
    }) || { data: null };

    return () => {
      isMounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [hashState?.accessToken, hashState?.refreshToken, hashState?.type]);

  const showSetPassword = isRecoveryLink || hasRecoverySession;

  const handleSendReset = async (e) => {
    e?.preventDefault();
    setError('');
    setMessage('');

    if (!email?.trim()) {
      setError('Vul je e-mailadres in.');
      return;
    }

    setIsSending(true);
    try {
      const redirectTo = `${window.location.origin}/auth/reset-password`;
      const { error: sendError } = await supabase?.auth?.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });

      if (sendError) throw sendError;

      setMessage('Check je e-mail voor de herstel-link (ook je spam).');
    } catch (err) {
      setError(err?.message || 'Kon geen herstel e-mail versturen.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setMessage('');

    if (!newPassword || newPassword.length < 6) {
      setError('Je wachtwoord moet minimaal 6 tekens zijn.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }

    setIsSaving(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser?.({ password: newPassword });
      if (updateError) throw updateError;

      setMessage('Wachtwoord bijgewerkt. Je kunt nu inloggen.');
      // Optional: send user back to auth page after a short delay
      setTimeout(() => navigate('/auth'), 800);
    } catch (err) {
      setError(err?.message || 'Wachtwoord bijwerken mislukt.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 16h4m-4-4h4m-4-4h4m-4-4h2a2 2 0 012 2v4m0 0a2 2 0 01-2 2h-2m0 0h-4m0 0a2 2 0 01-2-2V7a2 2 0 012-2h2m0 0h4"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">Wachtwoord herstellen</h2>
          <p className="mt-2 text-sm text-gray-600">
            {showSetPassword
              ? 'Kies een nieuw wachtwoord.'
              : 'Je krijgt een e-mail met een link om je wachtwoord te resetten.'}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {(error || message) && (
            <div
              className={`mb-4 border px-4 py-3 rounded-lg text-sm ${
                error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
              }`}
            >
              {error || message}
            </div>
          )}

          {!showSetPassword ? (
            <form onSubmit={handleSendReset} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value || '')}
                  placeholder="Enter your email"
                  className="w-full"
                />
              </div>

              <Button type="submit" disabled={isSending} className="w-full flex items-center justify-center">
                {isSending ? 'Versturen...' : 'Stuur herstel-link'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                <Link to="/auth" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                  Terug naar inloggen
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSetNewPassword} className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Nieuw wachtwoord
                </label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e?.target?.value || '')}
                  placeholder="Minimaal 6 tekens"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Bevestig wachtwoord
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value || '')}
                  placeholder="Herhaal je wachtwoord"
                  className="w-full"
                />
              </div>

              <Button type="submit" disabled={isSaving} className="w-full flex items-center justify-center">
                {isSaving ? 'Opslaan...' : 'Sla nieuw wachtwoord op'}
              </Button>

              <div className="text-center text-sm text-gray-600">
                <Link to="/auth" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                  Terug naar inloggen
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

